import { useState } from "react";
import { useLocation, useNavigate, redirect } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { ErrorBanner } from "~/components/ui/error-banner";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { authClient } from "../lib/auth-client";

type AuthErrorContext = { error: { message: string } };

function normalizeRedirectTo(value: string | null) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return "/app/convert";
  }
  return value;
}

export async function loader({ context, request }: LoaderFunctionArgs) {
  const env = (context as any).cloudflare?.env || {};
  const url = new URL(request.url);
  const redirectTo = normalizeRedirectTo(url.searchParams.get("redirectTo"));

  if (
    env.DEV_AUTO_LOGIN_EMAIL &&
    (env.NODE_ENV !== "production" || env.ALLOW_PROD_AUTO_LOGIN)
  ) {
    return redirect(`/api/auth/debug?redirectTo=${encodeURIComponent(redirectTo)}`);
  }

  return null;
}

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = normalizeRedirectTo(
    new URLSearchParams(location.search).get("redirectTo"),
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isMagicLink, setIsMagicLink] = useState(true);
  const [mailSent, setMailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const signIn = authClient.signIn as any;

      if (isMagicLink && !isSignUp) {
        await signIn.magicLink(
          {
            email,
            callbackURL: redirectTo,
          },
          {
            onSuccess: () => setMailSent(true),
            onError: (ctx: AuthErrorContext) => setError(ctx.error.message),
          },
        );
      } else if (isSignUp) {
        await authClient.signUp.email(
          {
            email,
            password,
            name: email.split("@")[0],
          },
          {
            onSuccess: () => navigate(redirectTo),
            onError: (ctx: AuthErrorContext) => setError(ctx.error.message),
          },
        );
      } else {
        await authClient.signIn.email(
          {
            email,
            password,
          },
          {
            onSuccess: () => navigate(redirectTo),
            onError: (ctx: AuthErrorContext) => setError(ctx.error.message),
          },
        );
      }
    } catch (e: any) {
      setError(e.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: "google" | "github") => {
    await authClient.signIn.social({
      provider,
      callbackURL: redirectTo,
    });
  };

  if (mailSent) {
    return (
      <div className="mx-auto flex min-h-svh w-full max-w-md items-center px-4 py-8">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Check your email</CardTitle>
            <CardDescription>
              We've sent a magic link to <strong>{email}</strong>. Click the link to sign in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="link" className="px-0" onClick={() => setMailSent(false)}>
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-svh w-full max-w-md items-center px-4 py-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{isSignUp ? "Create an account" : "Sign in to Fluentry"}</CardTitle>
          <CardDescription>Start your language learning journey today</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error ? <ErrorBanner message={error} /> : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {(!isMagicLink || isSignUp) ? (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            ) : null}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading
                ? "Processing..."
                : isSignUp
                  ? "Sign Up"
                  : isMagicLink
                    ? "Send Magic Link"
                    : "Sign In"}
            </Button>
          </form>

          {!isSignUp ? (
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => setIsMagicLink(!isMagicLink)}
                className="h-auto p-0"
              >
                {isMagicLink ? "Sign in with password instead" : "Sign in with magic link instead"}
              </Button>
            </div>
          ) : null}

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Separator className="flex-1" />
              <span>Or continue with</span>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button type="button" variant="outline" onClick={() => handleSocialSignIn("google")}>
                Google
              </Button>
              <Button type="button" variant="outline" onClick={() => handleSocialSignIn("github")}>
                GitHub
              </Button>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
            <Button
              type="button"
              variant="link"
              onClick={() => setIsSignUp(!isSignUp)}
              className="h-auto p-0"
            >
              {isSignUp ? "Sign in" : "Sign up"}
            </Button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
